import type { DossierUsageSmsSent } from "@rumble-it/common-lib/types/models/SmsSent.interface";
import type { ImageCaptureSent } from "@rumble-it/common-lib/types/models/ImageCaptureSent.interface";
import type { DwProSent } from "@rumble-it/common-lib/types/models/DwProSent.interface";
import { Button } from "@rumbledirect/ui";
import { Space } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { getEmailCorrespondenceSubject } from "../lib/helpers/getEmailCorrespondenceSubject";
import type { DossierUsageEmailSent } from "@rumble-it/common-lib/types/models/EmailSent.interface";

type CorrespondenceItemProps =
  | {
      dossierUsageType: "email";
      dossierUsageItems: DossierUsageEmailSent<string>[] | undefined;
      emptyStateText: string;
      openCorrespondenceModal?: (id: string, type: "email" | "sms") => void;
    }
  | {
      dossierUsageType: "sms";
      dossierUsageItems: DossierUsageSmsSent<string>[] | undefined;
      emptyStateText: string;
      openCorrespondenceModal?: (id: string, type: "email" | "sms") => void;
    }
  | {
      dossierUsageType: "ImageCapture";
      dossierUsageItems: ImageCaptureSent[] | undefined;
      emptyStateText: string;
      openCorrespondenceModal?: never;
    }
  | {
      dossierUsageType: "DwPro";
      dossierUsageItems: DwProSent[] | undefined;
      emptyStateText: string;
      openCorrespondenceModal?: never;
    };

//TODO: Improve type safety by using a union type for dossierUsageItems
export const CorrespondenceItem = ({
  dossierUsageItems,
  dossierUsageType,
  emptyStateText,
  openCorrespondenceModal,
}: CorrespondenceItemProps) => {
  const getSortKey = (item: (typeof dossierUsageItems)[number]): string => {
    if (dossierUsageType === "email") {
      const emailItem = item as DossierUsageEmailSent<string>;
      if (emailItem.emailSent?.createdAt) {
        return emailItem.emailSent.createdAt;
      }
    } else if (dossierUsageType === "ImageCapture") {
      const imageItem = item as ImageCaptureSent;
      if (imageItem.completedAt) {
        return imageItem.completedAt;
      }
    } else if (dossierUsageType === "DwPro") {
      const dwProItem = item as DwProSent;
      if (dwProItem.processedAt) {
        return dwProItem.processedAt;
      }
    }
    // SMS (no explicit timestamp exposed) and any item missing its preferred
    // timestamp fall back to @id, which for UUIDv7-based identifiers sorts
    // lexicographically by creation time.
    return item["@id"] ?? "";
  };

  return !dossierUsageItems || !dossierUsageItems?.length ? (
    <Space align="center">
      <CloseCircleOutlined style={{ color: "#ff4d4f" }} />
      <span>{emptyStateText}</span>
    </Space>
  ) : (
    <Space direction="vertical" size="small" className="w-full">
      {[...dossierUsageItems]
        .sort((a, b) => getSortKey(b).localeCompare(getSortKey(a)))
        .map((dossierUsageItem) => (
          <Space key={dossierUsageItem["@id"]} align="center">
            <CheckCircleOutlined style={{ color: "#52c41a" }} />
            {dossierUsageType === "email" && (
              <Button
                type="text"
                size="small"
                onClick={() => {
                  const emailItem =
                    dossierUsageItem as DossierUsageEmailSent<string>;
                  openCorrespondenceModal?.(
                    emailItem.emailSent?.id,
                    dossierUsageType,
                  );
                }}
                className="p-0 h-auto text-left whitespace-normal"
              >
                {getEmailCorrespondenceSubject(dossierUsageItem)}
              </Button>
            )}
            {dossierUsageType === "sms" && (
              <Button
                type="text"
                size="small"
                onClick={() => {
                  const smsItem =
                    dossierUsageItem as DossierUsageSmsSent<string>;
                  openCorrespondenceModal?.(
                    smsItem.smsSent?.id,
                    dossierUsageType,
                  );
                }}
                className="p-0 h-auto text-left whitespace-normal"
              >
                Details bekijken
              </Button>
            )}
            {dossierUsageType === "ImageCapture" && (
              <span>
                ({dossierUsageItems?.length}x){" "}
                {/* @ts-ignore // dossierUsageItem.soleraState is a string */}
                {dossierUsageItem.soleraState || "ImageCapture verzonden"}
              </span>
            )}
            {dossierUsageType === "DwPro" && (
              <p>
                {/* @ts-ignore // dossierUsageItem.dossierReferentie?.id is a string */}
                {dossierUsageItem.dwProSent?.dossierReferentie ?? (
                  <em>Onbekende referentie</em>
                )}
                {/* @ts-ignore // dossierUsageItem.dossierNr?.id is a string */}
                {dossierUsageItem.dwProSent?.dossierNr
                  ? //@ts-ignore // dossierUsageItem.dossierNr is a number
                    ` (DW Pro dossier nummer ${dossierUsageItem.dwProSent?.dossierNr})`
                  : ""}
                <br />
              </p>
            )}
          </Space>
        ))}
    </Space>
  );
};
